import { ARMS_DEALERS } from '../../../shared/economy';
import { gunOfAmmo, weaponOfItem } from '../../../shared/weapons';
import { WEAPONS, fenceStock, type WeaponId } from '../weaponData';
import { discounted } from '../../../shared/contacts';

/**
 * What lies on Sid's counter and what the buttons on the price card say (pure; the back-room system
 * and the tests use it). Offline the stock and prices are the fence's (weaponData.fenceStock); online
 * they are the arms dealer's in shared/economy.ts (the server's prices before supply pressure, so the
 * card says "about"), bought through the existing `supplierBuy` request.
 */

/** Browsing order = left to right in the room: the rack (long things), then the counter. */
export const ROOM_ORDER: readonly WeaponId[] = ['bat', 'crowbar', 'sawnoff', 'cosh', 'knife', 'revolver'];

/** Online: the arms dealer whose stock is on the counter. */
export const ONLINE_DEALER = 'arms-marlowe';

export interface CounterLine {
  weapon: WeaponId;
  /** What `buy` names: the fence item id offline (`revolver`), the item def online (`wpn-revolver`). */
  item: string;
  price: number;
  /** Online prices move with supply: the card says "about". */
  about: boolean;
  /** A box of rounds for a gun: what to ask for, how many, what it costs. */
  ammo: { item: string; qty: number; price: number; name: string } | null;
}

const byRoom = (a: CounterLine, b: CounterLine): number => ROOM_ORDER.indexOf(a.weapon) - ROOM_ORDER.indexOf(b.weapon);

/** `off`: Sid's discount for your standing with him (contact ladder, WP-X1), as a fraction. */
export function offlineLines(off = 0): CounterLine[] {
  const d = (n: number): number => (off > 0 ? discounted(n, off) : n);
  const stock = fenceStock();
  const out: CounterLine[] = [];
  for (const s of stock) {
    if (s.kind !== 'weapon' || !ROOM_ORDER.includes(s.weapon)) continue;
    const a = stock.find((x) => x.kind === 'ammo' && x.weapon === s.weapon);
    const f = WEAPONS[s.weapon].firearm;
    out.push({ weapon: s.weapon, item: s.id, price: d(s.price), about: false, ammo: a && f ? { item: a.id, qty: f.ammo.box, price: d(a.price), name: f.ammo.name } : null });
  }
  return out.sort(byRoom);
}

export function onlineLines(off = 0): CounterLine[] {
  const d = (n: number): number => (off > 0 ? discounted(n, off) : n);
  const dealer = ARMS_DEALERS.find((d) => d.id === ONLINE_DEALER);
  if (!dealer) return [];
  const out: CounterLine[] = [];
  for (const [def, price] of Object.entries(dealer.stock)) {
    const w = weaponOfItem(def);
    if (!w || !ROOM_ORDER.includes(w)) continue;
    const f = WEAPONS[w].firearm;
    const ammoDef = f ? Object.keys(dealer.stock).find((d) => gunOfAmmo(d) === w) : undefined;
    // The same box Sid sells offline (6 × .38, 4 × 12-bore), priced per round.
    const ammo = f && ammoDef ? { item: ammoDef, qty: f.ammo.box, price: d(dealer.stock[ammoDef]! * f.ammo.box), name: f.ammo.name } : null;
    out.push({ weapon: w, item: def, price: d(price), about: true, ammo });
  }
  return out.sort(byRoom);
}

export interface CardButtons {
  buy: { label: string; enabled: boolean };
  ammo: { label: string; enabled: boolean } | null;
  /** £ short for the weapon itself (0 when you can pay, or have it). */
  short: number;
  /** Why a button is grey, in plain words ('' when both are fine). */
  why: string;
}

const money = (n: number, about: boolean): string => `${about ? 'about ' : ''}£${n.toLocaleString('en-GB')}`;

/** The price card's buttons for a line, given what you carry and the cash in your pocket. */
export function cardButtons(line: CounterLine, s: { owned: boolean; room: number; cash: number }): CardButtons {
  const whys: string[] = [];
  let buy: CardButtons['buy'];
  let short = 0;
  if (s.owned) buy = { label: 'In your coat', enabled: false };
  else if (s.cash < line.price) {
    buy = { label: `Buy · £${line.price}`, enabled: false };
    short = line.price - s.cash;
    whys.push(`You’re £${line.price - s.cash} short.`);
  } else buy = { label: `Buy · £${line.price}`, enabled: true };
  let ammo: CardButtons['ammo'] = null;
  const a = line.ammo;
  if (a) {
    const label = `Box of ${a.qty} · £${a.price}`;
    if (!s.owned) ammo = { label, enabled: false };
    else if (s.room <= 0) {
      ammo = { label, enabled: false };
      whys.push('Your pockets are full of rounds.');
    } else if (s.cash < a.price) {
      ammo = { label, enabled: false };
      whys.push(`£${a.price - s.cash} short for a box.`);
    } else ammo = { label, enabled: true };
    if (!s.owned && !whys.length) whys.push(`Rounds once you have the ${WEAPONS[line.weapon].short.toLowerCase()}.`);
  }
  return { buy, ammo, short, why: whys.join(' ') };
}

/** The price line on the card ("£250", online "about £450"). */
export function priceText(line: CounterLine): string {
  return money(line.price, line.about);
}
